import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import {
  DATE_FORMATS_LUXON,
  formatISOFromUTC,
  formatISOLocal,
} from '@/utils/dateHelpers'
import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { clearAppliedChange } from './planChange.slice'
import {
  selectLegacyHeldTier,
  selectPlanCustomerType,
} from './planChange.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'
import { UNDISPLAYED_FEATURE_IDS } from '@/constants/productFeatureConstants'
import { displayRowKeysFor } from './taxFilingRows'
import { WITHHELD_REASON } from './withheldReasons'
import {
  audienceEyebrowsFor,
  planChangeBaseNamesFor,
  planChangeDisplayNamesFor,
  type AudienceEyebrows,
  type PlanSegment,
  type TierDisplayNames,
} from './tierDisplayNames'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectIsCurrentUserScorp,
  selectIsGroupPractice,
} from '@/selectors/user.selectors'
import {
  CORE_TIER_SEQUENCE,
  type AppliedPlanChange,
  type CurrentPlan,
  type PlanCadence,
  type PlanCatalogTier,
  type PlanChangeTarget,
  type PendingPlanChange,
  type PlanChangeTier,
  type WithheldLossRecord,
} from './planChange.types'

export enum PLAN_CHANGE_STEP {
  choose = 'choose',
  confirm = 'confirm',
  result = 'result',
}

export {
  TIER_DISPLAY_NAMES,
  tierDisplayNamesFor,
  type PlanSegment,
  type TierDisplayNames,
} from './tierDisplayNames'

export const useViewerPlanSegment = (): PlanSegment => {
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isGroup = useReselector(selectIsGroupPractice)
  return isScorp || isGroup ? 'scorp_group' : 'sole_prop'
}

const useNamesForLegacyTier = (
  legacyTier: PlanChangeTier | null
): TierDisplayNames => {
  const customerType = useReselector(selectPlanCustomerType)
  return useMemo(
    () => planChangeDisplayNamesFor(customerType, legacyTier),
    [customerType, legacyTier]
  )
}

/** Names for the plan page. The member's own plan wears "(Legacy)" on a legacy product, so index it for the current plan only. */
export const useTierDisplayNames = (): TierDisplayNames =>
  useNamesForLegacyTier(useReselector(selectLegacyHeldTier))

/** Names for plans the member is being offered, never the legacy name of the plan they hold. */
export const useCatalogTierDisplayNames = (): TierDisplayNames =>
  useNamesForLegacyTier(null)

/** Plain names for card and column titles: the held legacy tier keeps its suffix, the audience rides on a separate line. */
export const useBaseTierDisplayNames = (): TierDisplayNames => {
  const legacyTier = useReselector(selectLegacyHeldTier)
  return useMemo(() => planChangeBaseNamesFor(legacyTier), [legacyTier])
}

export const useAudienceEyebrows = (
  viewTiers: readonly PlanChangeTier[]
): AudienceEyebrows =>
  audienceEyebrowsFor(viewTiers, useReselector(selectPlanCustomerType))

export const planCardTitle = (
  tier: PlanChangeTier,
  productName: string | undefined,
  names: TierDisplayNames
): string =>
  CORE_TIER_SEQUENCE.includes(tier) ? names[tier] : (productName ?? names[tier])

export const planChangeCardTitle = (
  tier: PlanChangeTier,
  productName: string | undefined,
  names: TierDisplayNames
): string =>
  tier === 'lite_scorp_group'
    ? names[tier]
    : planCardTitle(tier, productName, names)

export const TIER_ORDER: Record<PlanChangeTier, number> = {
  entry: 0,
  starter: 1,
  basic: 2,
  lite: 3,
  essential: 4,
  essential_plus: 5,
  premium: 6,
  premium_plus: 7,
  lite_scorp_group: 3,
}

const MONTHS_PER_CADENCE: Record<PlanCadence, number> = {
  monthly: 1,
  quarterly: 3,
  annual: 12,
}

export const CADENCE_SUFFIX: Record<PlanCadence, string> = {
  monthly: '/mo',
  quarterly: '/qtr',
  annual: '/yr',
}

export const CADENCE_LABEL: Record<PlanCadence, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
}

export const CADENCE_PERIOD: Record<PlanCadence, string> = {
  monthly: 'month',
  quarterly: 'quarter',
  annual: 'year',
}

const CADENCE_ADVERB: Record<PlanCadence, string> = {
  monthly: 'monthly',
  quarterly: 'quarterly',
  annual: 'annually',
}

export const planWithCadenceLabel = (
  planName: string,
  cadence: PlanCadence
): string => `${planName}, ${CADENCE_LABEL[cadence]}`

export const BILLING_LINE: Record<PlanCadence, string> = {
  monthly: 'Billed monthly',
  quarterly: 'Billed every 3 months',
  annual: 'Billed once a year',
}

/**
 * How far a coupon reaches, counted in billing periods rather than months: it discounts
 * whole invoices, so three months of coupon covers an annual plan's entire first year, and
 * one invoice is the floor.
 */
export const promoSpanLabel = (
  durationInMonths: number,
  cadence: PlanCadence
): string => {
  const periods = Math.max(
    1,
    Math.ceil(durationInMonths / MONTHS_PER_CADENCE[cadence])
  )
  const noun = CADENCE_PERIOD[cadence]
  return periods === 1 ? noun : `${periods} ${noun}s`
}

/** Parsed as UTC: these are Stripe billing anchors, so a local parse can shift the day. */
export const formatPlanDate = (isoDate: string): string =>
  formatISOFromUTC(isoDate, DATE_FORMATS_LUXON.DISPLAY_SHORT)

/** Parsed in the viewer's own zone, unlike formatPlanDate: this is a real moment, not a billing anchor. */
export const formatReceiptDate = (isoDate: string): string =>
  formatISOLocal(isoDate, DATE_FORMATS_LUXON.DISPLAY_SHORT)

export const chargeLineCopy = (invoicedAmountCents: number): string => {
  if (invoicedAmountCents > 0) {
    return `You were charged ${formatCurrencyFromCents(invoicedAmountCents)} today.`
  }
  if (invoicedAmountCents < 0) {
    return `A ${formatCurrencyFromCents(Math.abs(invoicedAmountCents))} credit was applied to your account.`
  }
  return 'No charge was made.'
}

/** Not "nothing is billed": annual-paid-monthly installments keep charging until this date. */
export const scheduledLineCopy = (effectiveAt: string): string =>
  `This change isn't billed until it takes effect on ${formatPlanDate(effectiveAt)}.`

/** What the receipt's live region announces, word for word what its callout renders. */
export const receiptAnnouncement = (result: AppliedPlanChange): string =>
  result.status === 'applied'
    ? chargeLineCopy(result.invoicedAmountCents)
    : scheduledLineCopy(result.effectiveAt)

const CARD_BRAND_LABELS: Record<string, string> = {
  amex: 'Amex',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'Eftpos Australia',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
}

export const cardBrandLabel = (brand: string): string | undefined =>
  CARD_BRAND_LABELS[brand]

export const cardLabel = (
  brand: string | null,
  last4: string | null
): string | null => {
  if (!brand || !last4) return null
  const mask = `••••${last4}`
  const brandLabel = cardBrandLabel(brand)
  return brandLabel ? `${brandLabel} ${mask}` : mask
}

export const isAnnualPaidMonthly = (
  currentPlan: Pick<CurrentPlan, 'cadence' | 'inCommittedTerm'>
): boolean => currentPlan.cadence === 'monthly' && currentPlan.inCommittedTerm

const sameRowKeys = (own: string[], catalog: string[]): boolean => {
  const held = new Set(catalog)
  return own.length === catalog.length && own.every((key) => held.has(key))
}

/**
 * Legacy products keep their tier but not its rate or its feature set, so they wear a name
 * they outgrew. A plan matching the catalog on rate and on every row the table renders is
 * that tier, whatever product it is billed through: a second column for it would repeat the
 * first tick for tick.
 */
export const isLegacyPlan = (
  currentPlan: Pick<
    CurrentPlan,
    'cadence' | 'listAmountCents' | 'featureIds' | 'inCommittedTerm'
  >,
  tier: Pick<PlanCatalogTier, 'featureIds' | 'prices'>
): boolean => {
  if (!currentPlan.featureIds) return false
  // Committed at a monthly cadence is its own plan: the catalog's monthly plan is not the
  // one they hold, whatever the rate and the rows say.
  if (isAnnualPaidMonthly(currentPlan)) return true
  const catalogPrice = tier.prices.find(
    (price) => price.cadence === currentPlan.cadence
  )
  if (catalogPrice?.listAmountCents !== currentPlan.listAmountCents) {
    return true
  }
  // Once the catalog lists no phone support, an eligible customer's own copy of it is not a plan difference.
  const ownFeatureIds = tier.featureIds.includes(ProductFeatureId.phoneSupport)
    ? currentPlan.featureIds
    : currentPlan.featureIds.filter(
        (featureId) => featureId !== ProductFeatureId.phoneSupport
      )
  return !sameRowKeys(
    displayRowKeysFor(ownFeatureIds),
    displayRowKeysFor(tier.featureIds)
  )
}

const DIRECTION_KIND = {
  up: 'upgrade',
  down: 'downgrade',
  lateral: 'lateral',
} as const

/** The card's kind is the server's reading of the move; the page keeps no tier ladder. */
export const planCardKind = (
  target: Pick<PlanChangeTarget, 'direction'>
): 'upgrade' | 'downgrade' | 'lateral' => DIRECTION_KIND[target.direction]

/** A legacy member's own tier and billing, offered as the current catalog version of it. */
export const isCatalogTwin = (
  target: Pick<PlanChangeTarget, 'tier' | 'cadence'>,
  currentPlan: Pick<CurrentPlan, 'tier' | 'cadence' | 'inCommittedTerm'>
): boolean =>
  target.tier === currentPlan.tier &&
  target.cadence === currentPlan.cadence &&
  !isAnnualPaidMonthly(currentPlan)

export const BILLING_TABS: readonly PlanCadence[] = ['monthly', 'annual']

/** The tab of the member's own billing: a monthly payer under an annual commitment still reads monthly. */
export const defaultCadenceFor = (
  currentPlan: Pick<CurrentPlan, 'cadence'>
): PlanCadence => (currentPlan.cadence === 'annual' ? 'annual' : 'monthly')

export const compareTierRank = (a: PlanChangeTier, b: PlanChangeTier): number =>
  TIER_ORDER[a] - TIER_ORDER[b]

/**
 * The pending change's tier when a complete option set no longer offers it, so releasing the
 * change is final. A suppressed target may be a transient pricing failure, so it never counts.
 */
export const unofferedPendingTier = (
  pendingChange: PendingPlanChange | null,
  targets: PlanChangeTarget[],
  suppressedTargetCount: number
): PlanChangeTier | null =>
  pendingChange &&
  suppressedTargetCount === 0 &&
  !targets.some((t) => t.tier === pendingChange.toTier)
    ? pendingChange.toTier
    : null

export const pendingChangeFinalCopy = (
  tier: PlanChangeTier,
  names: TierDisplayNames
): string => `You won't be able to schedule a move to ${names[tier]} again.`

export const normalizeAmountToCadence = (
  amountCents: number,
  from: PlanCadence,
  to: PlanCadence
): number =>
  Math.round((amountCents / MONTHS_PER_CADENCE[from]) * MONTHS_PER_CADENCE[to])

export const perMonthCents = (
  amountCents: number,
  cadence: PlanCadence
): number => normalizeAmountToCadence(amountCents, cadence, 'monthly')

/**
 * The ongoing monthly difference against what the member pays now. Reads `effectiveAmountCents`
 * rather than a promo price so it describes the rate being committed to, not an introductory one.
 */
export const deltaChipCopy = (
  currentPlan: Pick<CurrentPlan, 'cadence' | 'effectiveAmountCents'>,
  target: Pick<PlanChangeTarget, 'cadence' | 'effectiveAmountCents'>
): { label: string; tone: 'gray' | 'green' } => {
  const differenceCents =
    perMonthCents(target.effectiveAmountCents, target.cadence) -
    perMonthCents(currentPlan.effectiveAmountCents, currentPlan.cadence)
  if (differenceCents === 0) {
    return { label: 'Same price per month', tone: 'gray' }
  }
  const amount = formatCurrencyFromCents(Math.abs(differenceCents), {
    hideDecimalsIfZero: true,
  })
  return differenceCents > 0
    ? { label: `+${amount}/mo`, tone: 'gray' }
    : { label: `-${amount}/mo`, tone: 'green' }
}

/**
 * Twelve months at what the member pays now, less the annual total. Measured against their
 * own rate because a legacy member pays below catalog. Null unless it is a real saving.
 */
export const annualSavingCents = (
  currentPlan: Pick<CurrentPlan, 'cadence' | 'effectiveAmountCents'>,
  target: Pick<PlanChangeTarget, 'cadence' | 'effectiveAmountCents'>
): number | null => {
  if (target.cadence !== 'annual' || currentPlan.cadence === 'annual') {
    return null
  }
  const yearAtCurrentRate =
    perMonthCents(currentPlan.effectiveAmountCents, currentPlan.cadence) * 12
  const savingCents = yearAtCurrentRate - target.effectiveAmountCents
  return savingCents > 0 ? savingCents : null
}

export const bestAnnualSavingCents = (
  currentPlan: Pick<CurrentPlan, 'cadence' | 'effectiveAmountCents'>,
  targets: Pick<PlanChangeTarget, 'cadence' | 'effectiveAmountCents'>[]
): number | null =>
  targets.reduce<number | null>((best, target) => {
    const saving = annualSavingCents(currentPlan, target)
    if (saving === null) return best
    return best === null ? saving : Math.max(best, saving)
  }, null)

const annualSavingAmountCopy = (savingCents: number): string =>
  `${formatCurrencyFromCents(savingCents, { hideDecimalsIfZero: true })}/yr`

export const annualSavingCopy = (savingCents: number): string =>
  `Save ${annualSavingAmountCopy(savingCents)}`

export const annualSavingsHintCopy = (bestSavingCents: number): string =>
  `Annual saves up to ${annualSavingAmountCopy(bestSavingCents)}`

export const billingLineCopy = (
  cadence: PlanCadence,
  totalCents: number
): string =>
  cadence === 'monthly'
    ? BILLING_LINE.monthly
    : `${formatCurrencyFromCents(totalCents)} ${BILLING_LINE[cadence].toLowerCase()}`

type DiscountCarryPlan = Pick<
  CurrentPlan,
  'discountLabel' | 'discountKind' | 'discountEndsAt' | 'cadence' | 'termEndsAt'
>

export const discountExpiryCopy = (
  discountEndsAt: string | null
): string | null =>
  discountEndsAt
    ? `Discount expires on ${formatPlanDate(discountEndsAt)}`
    : null

/**
 * Stripe takes a flat coupon's dollars off each invoice unscaled, so on a new cadence
 * "carries over" would hide what the discount is now worth.
 */
export const discountCarryCopy = (
  currentPlan: DiscountCarryPlan,
  target: Pick<PlanChangeTarget, 'cadence' | 'timing' | 'discountApplies'>,
  now: DateTime = DateTime.utc(),
  targetName?: string
): string | null => {
  const { discountLabel, discountEndsAt } = currentPlan
  if (!discountLabel) {
    return null
  }
  if (target.discountApplies === false) {
    return `Your ${discountLabel} doesn't apply to ${targetName ?? 'this plan'}`
  }
  const endsAt = discountEndsAt
    ? DateTime.fromISO(discountEndsAt, { zone: 'utc' })
    : null
  const firstInvoiceAt =
    target.timing === 'term_end'
      ? DateTime.fromISO(currentPlan.termEndsAt, { zone: 'utc' })
      : now
  if (endsAt && endsAt <= firstInvoiceAt) {
    return `Your ${discountLabel} ends before this change starts`
  }
  if (
    currentPlan.discountKind !== 'amount' ||
    target.cadence === currentPlan.cadence
  ) {
    const carries = `Your ${discountLabel} carries over${targetName ? ` to ${targetName}` : ''}`
    const expiry = discountExpiryCopy(discountEndsAt)
    return expiry ? `${carries}. ${expiry}` : carries
  }
  if (!discountEndsAt || !endsAt) {
    return `Your ${discountLabel} applies once per ${CADENCE_PERIOD[target.cadence]}`
  }
  const secondInvoiceAt = firstInvoiceAt.plus({
    months: MONTHS_PER_CADENCE[target.cadence],
  })
  if (endsAt <= secondInvoiceAt) {
    return `Your ${discountLabel} applies to your first ${CADENCE_LABEL[target.cadence].toLowerCase()} invoice only`
  }
  return `Your ${discountLabel} applies once per ${CADENCE_PERIOD[target.cadence]} until ${formatPlanDate(discountEndsAt)}`
}

/**
 * The line under the price explaining why it is what it is. A promo's post-offer price
 * wins: it is the figure that changes, and soon. Otherwise a durable discount is named by
 * its shape and stated to carry over, because Stripe moves the coupon with the plan.
 */
export const rateNoteCopy = (
  currentPlan: DiscountCarryPlan,
  target: Pick<
    PlanChangeTarget,
    'promo' | 'listAmountCents' | 'cadence' | 'timing' | 'discountApplies'
  >,
  targetName?: string
): string | null => {
  if (target.promo) {
    return `Then ${formatCurrencyFromCents(target.listAmountCents)}${CADENCE_SUFFIX[target.cadence]}`
  }
  // The card's own heading already names the plan; only a discount that won't follow needs it.
  return discountCarryCopy(
    currentPlan,
    target,
    DateTime.utc(),
    target.discountApplies === false ? targetName : undefined
  )
}

export const timingCopy = (
  target: Pick<PlanChangeTarget, 'timing'>,
  currentPlan: Pick<CurrentPlan, 'termEndsAt'>
): string =>
  target.timing === 'term_end'
    ? `Starts ${formatPlanDate(currentPlan.termEndsAt)}`
    : 'Starts today'

/**
 * An annual commitment reports a monthly cadence, so the plain monthly plan looks like the
 * schedule they are already on. It buys flexibility, not features.
 */
const switchesSchedule = (
  target: Pick<PlanChangeTarget, 'cadence'>,
  currentPlan: Pick<CurrentPlan, 'cadence' | 'inCommittedTerm'>
): boolean =>
  target.cadence !== currentPlan.cadence || isAnnualPaidMonthly(currentPlan)

/** A same-tier move that loses a feature costs the customer something, so it never reads as lateral. */
export const ctaLabelFor = (
  target: Pick<PlanChangeTarget, 'direction' | 'cadence' | 'lost'>,
  currentPlan: Pick<CurrentPlan, 'cadence' | 'inCommittedTerm'>,
  displayName: string
): string => {
  if (planCardKind(target) !== 'lateral' || target.lost.length > 0) {
    return `Move to ${displayName}`
  }
  return switchesSchedule(target, currentPlan)
    ? `Switch to ${CADENCE_LABEL[target.cadence].toLowerCase()} billing`
    : `Upgrade my ${displayName} Plan`
}

export const confirmTitleFor = (
  target: Pick<PlanChangeTarget, 'direction' | 'cadence' | 'lost'>,
  currentPlan: Pick<CurrentPlan, 'tier' | 'cadence' | 'inCommittedTerm'>,
  displayName: string,
  names: TierDisplayNames
): string => {
  if (planCardKind(target) === 'lateral' && target.lost.length === 0) {
    return switchesSchedule(target, currentPlan)
      ? `Switch ${names[currentPlan.tier]} to ${CADENCE_LABEL[
          target.cadence
        ].toLowerCase()} billing`
      : `Upgrade my ${displayName} Plan`
  }
  return target.cadence === currentPlan.cadence
    ? `Move to ${displayName}`
    : `Move to ${displayName}, billed ${CADENCE_ADVERB[target.cadence]}`
}

const costsNothing = ({ featureId, reason }: WithheldLossRecord): boolean =>
  reason === WITHHELD_REASON.stillDisplayed ||
  reason === WITHHELD_REASON.copyOnly ||
  UNDISPLAYED_FEATURE_IDS.includes(featureId)

/**
 * Neither reassurance holds unconditionally: a withheld loss is usually dropped without being
 * told, and a longer cadence starts a term that defers the next reduction.
 */
export const reassuranceCopy = (
  target: Pick<
    PlanChangeTarget,
    'cadence' | 'withheld' | 'direction' | 'timing'
  >,
  currentPlan: Pick<CurrentPlan, 'inCommittedTerm' | 'termEndsAt'>
): string | null => {
  const keepsEverything = target.withheld.every(costsNothing)
  const keeps = 'You keep everything not listed here'
  if (target.cadence !== 'monthly') {
    return keepsEverything ? `${keeps}.` : null
  }
  // A committed customer cannot change plans whenever they like, whatever the target's
  // cadence says: a later change waits for the term.
  if (currentPlan.inCommittedTerm) {
    if (target.timing !== 'term_end') {
      return keepsEverything ? `${keeps}.` : null
    }
    const term = `you're on an annual term, so ${termEndConsequence(currentPlan.termEndsAt)}`
    return keepsEverything
      ? `${keeps}, and ${term}.`
      : `${term.charAt(0).toUpperCase()}${term.slice(1)}.`
  }
  return keepsEverything
    ? `${keeps}, and you can change plans again whenever you like.`
    : 'You can change plans again whenever you like.'
}

const termEndConsequence = (termEndsAt: string): string =>
  `this change takes effect when your term ends on ${formatPlanDate(termEndsAt)}`

export const appliedPromoCopy = (label: string): string =>
  `${label}, applied when you confirm`

export const submitLabelFor = (
  target: Pick<PlanChangeTarget, 'timing' | 'lost' | 'prorationAmountCents'>
): string => {
  if (target.timing === 'term_end') return 'Schedule this change'
  if (target.lost.length > 0) return 'Confirm and change my plan'
  const proration = target.prorationAmountCents ?? 0
  if (proration > 0) {
    return `Confirm and pay ${formatCurrencyFromCents(proration)}`
  }
  return 'Confirm these changes'
}

export const moneyLineCopy = (
  target: Pick<PlanChangeTarget, 'timing' | 'prorationAmountCents'>,
  currentPlan: Pick<CurrentPlan, 'currentPeriodEnd' | 'termEndsAt'>
): string => {
  if (target.timing === 'term_end') {
    return `No charge today. Your plan changes on ${formatPlanDate(currentPlan.termEndsAt)}.`
  }
  // A credit lands on the next invoice, which is not the end of a committed term.
  const changeDate = formatPlanDate(currentPlan.currentPeriodEnd)
  const amount = target.prorationAmountCents ?? 0
  if (amount > 0) {
    return `You'll be charged ${formatCurrencyFromCents(amount)} today.`
  }
  if (amount < 0) {
    return `You'll get a ${formatCurrencyFromCents(Math.abs(amount))} credit applied to your next invoice on ${changeDate}.`
  }
  return 'No charge today. Your new plan starts now.'
}

interface ProrationBreakdownRow {
  label: string
  amount: string
}

/** Only a charge today is split: a credit-only change already names its one amount. */
export const prorationBreakdownRows = (
  target: Pick<
    PlanChangeTarget,
    | 'tier'
    | 'timing'
    | 'prorationAmountCents'
    | 'prorationCreditCents'
    | 'prorationChargeCents'
  >,
  currentPlan: Pick<CurrentPlan, 'tier'>,
  currentNames: TierDisplayNames,
  targetNames: TierDisplayNames
): ProrationBreakdownRow[] => {
  const { prorationCreditCents, prorationChargeCents } = target
  if (
    target.timing !== 'immediate' ||
    (target.prorationAmountCents ?? 0) <= 0 ||
    typeof prorationCreditCents !== 'number' ||
    typeof prorationChargeCents !== 'number'
  ) {
    return []
  }
  return [
    {
      label: `Credit for unused time on ${currentNames[currentPlan.tier]}`,
      amount: formatCurrencyFromCents(prorationCreditCents),
    },
    {
      label: `${targetNames[target.tier]} for the rest of this billing period`,
      amount: formatCurrencyFromCents(prorationChargeCents),
    },
  ]
}

export const usePlanChangeNavigation = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const goToStep = useCallback(
    (step: PLAN_CHANGE_STEP, state?: Record<string, unknown>) =>
      navigate(`/plan-change?step=${step}`, { state }),
    [navigate]
  )
  const resetFlow = useCallback(
    (state?: { conflict: true }) => {
      dispatch(clearAppliedChange())
      // Narrowed, not forwarded: this is handed straight to onClick in places, and a
      // MouseEvent in history state throws DataCloneError in a browser (jsdom will not).
      const conflict = state?.conflict === true ? { conflict: true } : undefined
      navigate(`/plan-change?step=${PLAN_CHANGE_STEP.choose}`, {
        replace: true,
        state: conflict,
      })
    },
    [navigate, dispatch]
  )
  return { goToStep, resetFlow }
}
